import {AfterViewInit, Component, computed, inject, OnDestroy, PLATFORM_ID, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {SwUpdate, VersionEvent} from '@angular/service-worker';
import {supportedLangs} from '@guards';
import {IonApp, IonRouterOutlet, ToastController} from '@ionic/angular/standalone';
import {LoggerService, StorageService, TranslateHelperService} from '@services';
import {NavigationEnd, Router} from '@angular/router';
import {MenuComponent} from './menu/menu.component';
import {MobileTabBarComponent} from '@components';
import {ProfileService} from '@services';
import {environment} from '../environments/environment';


	@Component({
	           selector: 'app-root',
	           standalone: true,
	           templateUrl: './app.component.html',
	           imports: [
		           IonRouterOutlet,
		           IonApp,
		           MenuComponent,
		           MobileTabBarComponent
	           ],
	           styleUrls: ['./app.component.scss']
           })
export class AppComponent implements AfterViewInit, OnDestroy {
	dark = true;
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	private toastController = this.isBrowser ? inject(ToastController) : null;
	private versionUpdatesSub?: { unsubscribe: () => void };
	private storageService = inject(StorageService);
	private swUpdate = this.isBrowser ? inject(SwUpdate) : null;
	private translateService: TranslateHelperService = inject(TranslateHelperService);
	private router = inject(Router);
	private readonly logger = inject(LoggerService);
	private readonly profileService = inject(ProfileService, {optional: true});
	private readonly currentRoute = signal<string>(this.router.url);
	private updateCheckIntervalId?: number;
	private readonly onWindowFocus = () => {
		this.checkForUpdates();
	};
	private readonly onWindowOnline = () => {
		this.checkForUpdates();
	};
	readonly showMenu = computed(() => {
		if (!this.profileService) {
			return false;
		}
		const profile = this.profileService.profile();
		if (!profile) {
			return false;
		}
		return this.isAppRoute(this.currentRoute());
	});
	readonly showMobileTabs = computed(() => this.showMenu());
	private routerEventsSub?: { unsubscribe: () => void };

	constructor() {
	}

	async ngAfterViewInit() {
		this.routerEventsSub = this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.currentRoute.set(event.urlAfterRedirects);
			}
		});
		if (!this.isBrowser) {
			return;
		}

		// Prefer language from URL, fallback to storage or detection
		const urlLang = this.router.url.split('/').filter(Boolean)[0];
		let lang = supportedLangs.includes(urlLang) ? urlLang : (this.storageService.get('currentLang') || null);
		if (!lang) {
			lang = this.detectLanguage();
		}

		// Set language for all users (connected and non-connected)
		// This will also save to localStorage via translateService
		await this.translateService.switchLanguage(lang);

		// Check for updates on initialization
		if (this.swUpdate && this.swUpdate.isEnabled) {
			this.checkForUpdates();
		}

		// Subscribe to version updates
		if (this.swUpdate && this.toastController) {
			this.versionUpdatesSub = this.swUpdate.versionUpdates.subscribe(async (versionEvent: VersionEvent) => {
				if (versionEvent.type !== 'VERSION_READY') {
					return;
				}

				const toast = await this.toastController!.create({
					                                              message: await this.translateService.instant('Mise à jour disponible !'),
					                                              position: 'bottom',
					                                              buttons: [
						                                              {
							                                              role: 'cancel',
							                                              text: await this.translateService.instant('Recharger')
						                                              }
					                                              ],
					                                              cssClass: 'yourCssClassName'
				                                              });
				await toast.present();

				toast
					.onDidDismiss()
					.then(() => this.swUpdate?.activateUpdate())
					.then(() => window.caches.keys().then(k => k.forEach(ke => window.caches.delete(ke))).then(() => window.location.reload()));
			});
		}

		// Check for updates periodically (every 6 hours)
		if (this.swUpdate && this.swUpdate.isEnabled) {
			this.updateCheckIntervalId = window.setInterval(() => {
				this.checkForUpdates();
			}, 6 * 60 * 60 * 1000); // 6 hours
		}

		// Check for updates when window gains focus
		if (this.swUpdate && this.swUpdate.isEnabled && typeof window !== 'undefined') {
			window.addEventListener('focus', this.onWindowFocus);

			// Check for updates when coming back online
			window.addEventListener('online', this.onWindowOnline);
		}

		const getCssVar = (name: string, fallback: string): string => {
			const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
			return value || fallback;
		};

		let addCss = '';
		let version = '';
		if (environment.testEnv) {
			addCss = `color: #ffa21a; font-size: ${getCssVar('--font-size-0-875', '0.875rem')}; font-weight: 600;`;
			version = 'Testing version';
		}
		if (!environment.testEnv && !environment.production) {
			addCss = `color: #5eb762; font-size: ${getCssVar('--font-size-0-875', '0.875rem')}; font-weight: 600;`;
			version = 'Dev version';
		}
		const css = `color: #e91e3c; font-size: ${getCssVar('--font-size-1-75', '1.75rem')}; font-weight: 700; font-style: italic; text-transform: uppercase; letter-spacing: 0.05em;`;
		const warning = await this.translateService.instant('warning');
		const warningCss = `color: #a0a0a0; font-size: ${getCssVar('--font-size-0-75', '0.75rem')}; line-height: ${getCssVar('--line-height-normal', '1.5')};`;
		this.logger.info('%cslaveous\n%c' + version + '\n\n%c' + warning, css, addCss, warningCss);
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new Event('landingShellReplace'));
		}
	}

	rightCLick($event: MouseEvent) {
		if (environment.production) {
			$event.preventDefault();
		}
	}

	ngOnDestroy(): void {
		this.versionUpdatesSub?.unsubscribe();
		this.routerEventsSub?.unsubscribe();
		if (this.isBrowser && typeof window !== 'undefined') {
			if (this.updateCheckIntervalId !== undefined) {
				clearInterval(this.updateCheckIntervalId);
			}
			window.removeEventListener('focus', this.onWindowFocus);
			window.removeEventListener('online', this.onWindowOnline);
		}
	}

	detectLanguage(): string {
		const lang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
		const normalized = lang.toLowerCase();
		const primary = normalized.split('-')[0];
		if (supportedLangs.includes(primary)) {
			return primary;
		}
		const matched = supportedLangs.find((supportedLang) => normalized.startsWith(supportedLang));
		return matched || 'en';
	}

	/**
	 * Check for service worker updates
	 */
	private async checkForUpdates(): Promise<void> {
		if (!this.swUpdate || !this.swUpdate.isEnabled) {
			return;
		}

		try {
			const updateAvailable = await this.swUpdate.checkForUpdate();
			if (updateAvailable) {
				this.logger.info('New version available');
			}
		} catch (error) {
			this.logger.error('Error checking for updates:', error);
		}
	}

	private isAppRoute(route: string): boolean {
		const normalized = route.split('?')[0].split('#')[0].replace(/\/+$/, '');
		const parts = normalized.split('/').filter(Boolean);
		if (!parts.length) {
			return false;
		}
		const firstSegment = parts[0].toLowerCase();
		const hasLangSegment = supportedLangs.includes(firstSegment);
		const section = hasLangSegment ? parts[1] : parts[0];
		return section === 'app';
	}
}
